import classNames from 'classnames';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Rarity } from '@sorare/core/src/__generated__/globalTypes';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import {
  Horizontal,
  SBHorizontal,
  Vertical,
} from '@sorare/core/src/atoms/layout/flex';
import { LinkBox, LinkOverlay } from '@sorare/core/src/atoms/navigation/Box';
import { HeadlineM, LabelL, LabelS } from '@sorare/core/src/atoms/typography';
import { ScarcityLabel } from '@sorare/core/src/components/collections/ScarcityLabel';

const StyledLinkBox = styled(LinkBox)`
  transition: transform 0.2s ease-in-out;

  &:hover:not(.snapshotted),
  &:focus:not(.snapshotted) {
    color: inherit;
    transform: scale(1.02);
  }
`;
const EmptyCollectionRoot = styled.div`
  height: 100%;
  background: var(--c-nd-50);
  padding: var(--double-unit);
  border-radius: var(--double-unit);
  display: grid;
  grid-template-rows: calc(5 * var(--unit)) 1fr calc(5 * var(--unit));
`;

const ImgWrapper = styled(Horizontal).attrs({ gap: 0, center: true })`
  height: 80px;
  margin: auto 0 var(--unit);
`;
const EmptyCollectionImgWrapper = styled(ImgWrapper)`
  margin: 0;
`;

const StyledImg = styled.img`
  width: 80px;
  height: 80px;
  object-fit: contain;
`;

const StyledSBHorizontal = styled(SBHorizontal)`
  align-items: flex-start;
`;

type Props = {
  rarity: Rarity | null;
  displayedStartYear?: number;
  displayedEndYear?: number | null;
  pictureUrl?: string | null;
  name: string;
  snapshotted: boolean;
  to?: string;
};

export const EmptyCollection = ({
  rarity,
  displayedStartYear,
  displayedEndYear,
  pictureUrl,
  name,
  snapshotted,
  to,
}: Props) => {
  return (
    <StyledLinkBox className={classNames({ snapshotted })}>
      <EmptyCollectionRoot>
        <StyledSBHorizontal>
          <LabelS>
            <ScarcityLabel scarcity={rarity || Rarity.custom_series} />
          </LabelS>
          <Vertical gap={0}>
            <LabelL bold>{displayedStartYear}</LabelL>
            {!!displayedEndYear && <LabelL bold>{displayedEndYear}</LabelL>}
          </Vertical>
        </StyledSBHorizontal>
        <Vertical gap={2} center>
          <EmptyCollectionImgWrapper>
            {!!pictureUrl && <StyledImg alt="" src={pictureUrl} />}
          </EmptyCollectionImgWrapper>
          <Vertical gap={0} className="text-center">
            <HeadlineM>{name}</HeadlineM>
            <LabelL color="var(--c-nd-600)">
              {!!snapshotted && (
                <FormattedMessage
                  id="CollectionPreview.noCollection"
                  defaultMessage="No Collection"
                />
              )}
            </LabelL>
          </Vertical>
          {!snapshotted && (
            <LinkOverlay as={Button} size="small" color="quaternary" to={to}>
              <FormattedMessage
                id="CollectionPreview.emptyCollectionCta"
                defaultMessage="Start Collection"
              />
            </LinkOverlay>
          )}
        </Vertical>
      </EmptyCollectionRoot>
    </StyledLinkBox>
  );
};
